import { Button, CircularProgress } from "@mui/material";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  CmpProspectNoteReturn,
  CmpProspectStaffOptionReturn,
} from "../../../graphql/types";
import CalendarDateInput from "../../common/dashboard/CalendarDateInput";
import {
  DataTableFeedback,
  dataTableFeedbackContainerSx,
} from "../../common/dashboard/DataTable";
import { SectionShell } from "../../common/dashboard/SectionShell";
import MemberEmailCaptureNotice from "../Members/MemberEmailCaptureNotice";
import { cmpShortDateFormatter } from "../../common/dashboard/formatters";
import InvoiceSearchableSelect from "../Invoicing/InvoiceSearchableSelect";
import {
  ProspectSortGlyph,
  prospectGoldButtonSx,
  prospectNoOptionsSx,
  prospectSelectMenuSx,
  prospectSelectSx,
} from "./prospectDesign";

export type ProspectNotesSectionProps = Readonly<{
  mode: "create" | "update" | "converted";
  staffOptions: readonly CmpProspectStaffOptionReturn[];
  notes: readonly CmpProspectNoteReturn[];
  notesLoading?: boolean;
  notesError?: string;
  comment: string;
  onCommentChange: (value: string) => void;
  isSaving: boolean;
  commentsSaved: boolean;
  saveError?: string;
}>;

type NoteSort = {
  column: "date" | "staff";
  direction: "asc" | "desc";
};

// CMP Dashboard (15) D:346-352: Date, Made By, Type, Details.
const noteHistoryGridClassName =
  "grid grid-cols-[minmax(110px,0.6fr)_minmax(140px,0.8fr)_minmax(110px,0.6fr)_minmax(0,2.4fr)]";

type ProspectNoteCommentProps = Readonly<{
  note: CmpProspectNoteReturn;
  expanded: boolean;
  onToggle: () => void;
}>;

const ProspectNoteComment = ({ note, expanded, onToggle }: ProspectNoteCommentProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    let active = true;
    const measure = () => {
      if (!active || !body.getBoundingClientRect().width) return;
      // scrollHeight gives the full text height even under the line clamp.
      // Measure against two lines (design line clamp) in either state, so
      // expansion, font loading, and column resizing cannot leave a toggle on
      // a fully visible note.
      const lineHeight = Number.parseFloat(window.getComputedStyle(body).lineHeight);
      setHasOverflow(body.scrollHeight > Math.ceil(lineHeight * 2));
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(body);
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    document.fonts?.addEventListener("loadingdone", measure);
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      document.fonts?.removeEventListener("loadingdone", measure);
    };
  }, [note.comment]);

  return <>
    <div
      ref={bodyRef}
      id={`prospect-note-body-${note.id}`}
      style={!expanded ? {
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      } : undefined}
    >
      {note.comment}
    </div>
    {hasOverflow && <button
      type="button"
      aria-expanded={expanded}
      aria-controls={`prospect-note-body-${note.id}`}
      onClick={onToggle}
      className="pt-0.5 text-[12px] font-bold leading-[normal] text-[#E1A731] outline-none focus-visible:ring-2 focus-visible:ring-[#E1A731]"
    >
      {expanded ? "Read Less" : "Read More"}
    </button>}
  </>;
};

export const ProspectNotesSection = ({
  mode,
  staffOptions,
  notes,
  notesLoading = false,
  notesError,
  comment,
  onCommentChange,
  isSaving,
  commentsSaved,
  saveError,
}: ProspectNotesSectionProps): React.ReactElement => {
  const [dateFilter, setDateFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("");
  const [keywordFilter, setKeywordFilter] = useState("");
  const [sort, setSort] = useState<NoteSort | null>(null);
  const [expandedNoteIds, setExpandedNoteIds] = useState<Set<string>>(
    () => new Set(),
  );

  const staffNames = useMemo(() => {
    const names = new Set(
      staffOptions
        .map(({ firstName, lastName }) => `${firstName} ${lastName}`.trim())
        .filter(Boolean),
    );
    // Author snapshots remain filterable after a staff account is removed or
    // renamed. The stored note remains the source of its original author name.
    notes.forEach(({ staffDisplayName }) => names.add(staffDisplayName));
    return Array.from(names).sort((left, right) =>
      left.localeCompare(right, "en-CA", { sensitivity: "base" }),
    );
  }, [notes, staffOptions]);

  const visibleNotes = useMemo(() => {
    const keyword = keywordFilter.trim().toLocaleLowerCase();
    const filtered = notes.filter((note) => {
      if (staffFilter && note.staffDisplayName !== staffFilter) return false;
      if (keyword && !note.comment.toLocaleLowerCase().includes(keyword)) {
        return false;
      }
      if (dateFilter) {
        const noteDate = new Date(note.createdAtUtcMilli);
        const [year, month, day] = dateFilter.split("-").map(Number);
        if (
          noteDate.getFullYear() !== year ||
          noteDate.getMonth() !== month - 1 ||
          noteDate.getDate() !== day
        ) {
          return false;
        }
      }
      return true;
    });
    return [...filtered].sort((left, right) => {
      if (!sort) return right.createdAtUtcMilli - left.createdAtUtcMilli;
      const comparison =
        sort.column === "date"
          ? left.createdAtUtcMilli - right.createdAtUtcMilli
          : left.staffDisplayName.localeCompare(right.staffDisplayName, "en-CA", {
              sensitivity: "base",
            });
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [dateFilter, keywordFilter, notes, sort, staffFilter]);

  const handleSort = (column: NoteSort["column"]) => {
    setSort((current) => ({
      column,
      direction:
        current?.column === column && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleClearFilters = () => {
    setDateFilter("");
    setStaffFilter("");
    setKeywordFilter("");
  };

  const handleToggleNote = (id: string) => {
    setExpandedNoteIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <SectionShell
      id="prospect-staff-notes"
      title="Prospect Communication Log"
      description={<MemberEmailCaptureNotice entity="prospect" />}
      variant="design"
      contentGap={18}
      className="[&>div>div>h3]:leading-[normal]"
    >
      {/* CMP Dashboard (15) D:313-327 (Monday D24): the note saves with
          Create Prospect / Update Prospect; no separate Save button. */}
      {mode !== "converted" && (
        <>
          <div className="flex min-w-0 flex-col gap-1.5">
            <label
              htmlFor="prospect-note-comment"
              className="text-[14px] font-bold leading-[normal] text-[#0A0A0A]"
            >
              Conversation Notes and Comments <span className="text-[#F0202E]">*</span>
            </label>
            <p className="text-[14px] leading-[normal] text-[#0A0A0A]">
              Enter your phone conversation, notes, or comments about this prospect.
            </p>
            <textarea
              id="prospect-note-comment"
              value={comment}
              onChange={(event) => onCommentChange(event.target.value)}
              disabled={isSaving}
              aria-required="true"
              aria-invalid={Boolean(saveError)}
              aria-describedby={saveError ? "prospect-note-error" : undefined}
              placeholder="Enter your phone conversation, notes, or comments"
              rows={3}
              className={`box-border min-h-[76px] w-full resize-y text-ellipsis rounded-lg border bg-white px-3 py-[11px] text-[14px] font-normal leading-[normal] text-[#0A0A0A] outline-none placeholder:text-[#757575] focus:border-[#E1A731] disabled:cursor-not-allowed disabled:bg-[#F7F7F8] ${saveError ? "border-[#E24C4C]" : "border-[#9E9E9E]"}`}
            />
            {saveError && (
              <p id="prospect-note-error" role="alert" data-prospect-field-error="true" className="text-[12px] leading-[normal] text-[#F21B1B]">
                {saveError}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div className="flex flex-wrap items-center gap-2.5">
              <p className="text-[14px] leading-[normal] text-[#0A0A0A]">
                The date and staff member are recorded automatically when the note is added.
              </p>
              {isSaving && (
                <span role="status" className="flex items-center gap-2 text-[13px] text-[#727272]">
                  <CircularProgress size={14} color="inherit" />
                  Saving...
                </span>
              )}
              {commentsSaved && (
                <span role="status" className="text-[13px] font-bold leading-[normal] text-[#10B981]">
                  Comments saved.
                </span>
              )}
            </div>
          </div>
        </>
      )}
      {mode !== "create" && (
        <>
          <h3 className="mt-1.5 text-[15px] font-bold leading-[normal] text-[#0A0A0A]">
            Notes, Comments &amp; Emails History
          </h3>
          <div className="grid grid-cols-1 items-end gap-[14px] md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)_auto]">
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="prospect-notes-date" className="text-[13px] font-bold leading-[normal] text-[#0A0A0A]">
                Filter by Date
              </label>
              <CalendarDateInput
                id="prospect-notes-date"
                ariaLabel="Filter by Date"
                mode="fullDate"
                appearance="claude"
                displayFormat="long"
                yearRange="historical"
                highlightTodayWhenEmpty
                value={dateFilter}
                onChange={setDateFilter}
                placeholder="Select Date"
                placeholderClassName="text-[#9E9E9E]"
                inputClassName="box-border w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border border-[#ECECEC] bg-white py-[11px] pl-[14px] pr-[38px] text-[14px] font-normal leading-[normal] text-[#0A0A0A] outline-none focus:border-[#E1A731]"
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label id="prospect-notes-staff-label" htmlFor="prospect-notes-staff" className="text-[13px] font-bold leading-[normal] text-[#0A0A0A]">
                Filter by Staff Member
              </label>
              <InvoiceSearchableSelect
                id="prospect-notes-staff"
                labelledById="prospect-notes-staff-label"
                options={staffNames}
                optionRowHeight={40}
                outlinedArrow
                getOptionKey={(name) => name}
                getOptionLabel={(name) => name}
                value={staffFilter || null}
                onChange={(name) => setStaffFilter(name || "")}
                placeholder="All Staff"
                searchPlaceholder="Search Staff"
                noOptionsText="No matches found"
                noOptionsSx={prospectNoOptionsSx}
                menuSx={prospectSelectMenuSx}
                sx={prospectSelectSx}
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="prospect-notes-keywords" className="text-[13px] font-bold leading-[normal] text-[#0A0A0A]">
                Search Keywords
              </label>
              <input
                id="prospect-notes-keywords"
                type="text"
                value={keywordFilter}
                onChange={(event) => setKeywordFilter(event.target.value)}
                placeholder="Search comments, notes, and email subjects"
                className="box-border w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border border-[#9E9E9E] bg-white px-3 py-[11px] text-[14px] font-normal leading-[normal] text-[#0A0A0A] outline-none placeholder:text-[#9E9E9E] focus:border-[#E1A731]"
              />
            </div>
            <Button
              type="button"
              onClick={handleClearFilters}
              sx={{
                ...prospectGoldButtonSx,
                backgroundColor: "#F21B1B",
                fontWeight: 700,
                height: 42,
                padding: "12px 22px",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#D01414", boxShadow: "none" },
              }}
            >
              Clear Filters
            </Button>
          </div>
          {notesError && (
            <p role="alert" className="text-[14px] leading-[1.55] text-[#E24C4C]">
              {notesError}
            </p>
          )}
          <div className="overflow-x-auto rounded-[10px] border border-[#ECECEC]" style={dataTableFeedbackContainerSx}>
            <table aria-label="Notes, Comments and Emails History" className="block w-full min-w-[740px] border-collapse text-left">
              <thead className="block bg-[#F7F7F8]">
                <tr className={noteHistoryGridClassName}>
                  {([
                    { column: "date", label: "Date" },
                    { column: "staff", label: "Made By" },
                  ] as const).map(({ column, label }) => (
                    <th
                      key={column}
                      scope="col"
                      aria-sort={sort?.column === column ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
                      className="min-w-0 p-0"
                    >
                      <button
                        type="button"
                        title="Sort"
                        onClick={() => handleSort(column)}
                        className="flex w-full items-center gap-1.5 px-[14px] py-3 text-left text-[13px] font-bold leading-[normal] text-[#0A0A0A] outline-none focus-visible:ring-2 focus-visible:ring-[#E1A731]"
                      >
                        {label}
                        <ProspectSortGlyph active={sort?.column === column} direction={sort?.direction || "asc"} />
                      </button>
                    </th>
                  ))}
                  <th scope="col" className="px-[14px] py-3 text-[13px] font-bold leading-[normal] text-[#0A0A0A]">
                    Type
                  </th>
                  <th scope="col" className="px-[14px] py-3 text-[13px] font-bold leading-[normal] text-[#0A0A0A]">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="block">
                {visibleNotes.map((note) => {
                  const expanded = expandedNoteIds.has(note.id);
                  return (
                    <tr key={note.id} className={`${noteHistoryGridClassName} border-t border-[#ECECEC]`}>
                      <td className="whitespace-nowrap px-[14px] py-[13px] text-[13px] font-normal leading-[normal] text-[#0A0A0A]">
                        {cmpShortDateFormatter.format(note.createdAtUtcMilli)}
                      </td>
                      <td className="min-w-0 break-words px-[14px] py-[13px] text-[13px] font-semibold leading-[normal] text-[#0A0A0A]">
                        {note.staffDisplayName}
                      </td>
                      <td className="whitespace-nowrap px-[14px] py-[13px] text-[13px] font-normal leading-[normal] text-[#0A0A0A]">
                        Notes/Comments
                      </td>
                      <td className="min-w-0 break-words px-[14px] py-[13px] text-[13px] font-normal leading-[1.5] text-[#0A0A0A]">
                        <ProspectNoteComment note={note} expanded={expanded} onToggle={() => handleToggleNote(note.id)} />
                      </td>
                    </tr>
                  );
                })}
                {(notesLoading || (!notesError && visibleNotes.length === 0)) && (
                  <tr className="block border-t border-[#ECECEC]">
                    <td colSpan={4} className="block p-0 text-[14px] leading-[normal] text-[#727272]">
                      <DataTableFeedback>
                        <div className="px-[14px] py-[18px]">
                          {notesLoading ? (
                            <span role="status" className="flex items-center gap-2">
                              <CircularProgress size={16} color="inherit" />
                              Loading notes...
                            </span>
                          ) : (
                            "No comments, notes, or emails match the current filters."
                          )}
                        </div>
                      </DataTableFeedback>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </SectionShell>
  );
};

export default ProspectNotesSection;
